import { MailUnreadIcon } from '@slite/icons'
import classNames from 'classnames'
import {
  FieldValues,
  useController,
  UseControllerProps,
  useFormState,
} from 'react-hook-form'

import { Input } from '..'
import { emailPattern } from '../../utils'
import { InputProps } from '../Input'
import { Form } from './Form'
import { useIntl } from 'react-intl'

type EmailInputProps<T extends FieldValues> = Pick<
  UseControllerProps<T>,
  'control' | 'rules' | 'name'
> & {
  className?: string
} & InputProps

export function EmailInput<T extends FieldValues>({
  name,
  control,
  className,
  rules,
  style,
  autoFocus = true,
  ...props
}: EmailInputProps<T>) {
  const intl = useIntl()
  const { field, fieldState } = useController({
    name,
    control,
    rules: {
      ...rules,
      required: {
        value: true,
        message: intl.formatMessage({
          id: 'boarding.form.email.required',
          defaultMessage: 'Please enter a valid email',
        }),
      },
      pattern: {
        value: emailPattern,
        message: intl.formatMessage({
          id: 'boarding.form.email.pattern',
          defaultMessage: 'Please enter a valid email',
        }),
      },
    },
  })

  const { isSubmitted } = useFormState({ control })

  const errorMessage =
    fieldState.isDirty &&
    fieldState.invalid &&
    isSubmitted &&
    fieldState.error?.message

  return (
    <>
      <Input
        className={classNames('margin-bottom-12 icon', className)}
        type="email"
        placeholder={intl.formatMessage({
          id: 'boarding.form.email.placeholder',
          defaultMessage: 'Your work email',
        })}
        autoComplete="email"
        error={Boolean(errorMessage)}
        autoFocus={autoFocus}
        data-test-id={`${name}Field`}
        style={style}
        renderLeft={() => (
          <MailUnreadIcon className="shade-40 margin-left-16" />
        )}
        {...field}
        {...props}
      />
      {errorMessage && <Form.Error message={errorMessage} />}
    </>
  )
}
